import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { patientApi, patientErr } from "@/lib/patientApi";
import { Heart, SignOut, Devices, ShieldCheck, WarningCircle, MusicNotes, LockKey, CaretRight, Receipt, Storefront } from "@phosphor-icons/react";
import PwaRefreshButton from "@/components/PwaRefreshButton";

const inputCls = "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] px-4 py-3 text-sm text-slate-800 outline-none focus:border-[#EC4899] focus:bg-white focus:ring-2 focus:ring-[#EC4899]/15";

export default function AkunSaya() {
  const nav = useNavigate();
  const [me, setMe] = useState(undefined);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("patient_token")) { nav("/pasien/masuk"); return; }
    patientApi.get("/patient/auth/me").then((r) => setMe(r.data))
      .catch(() => { localStorage.removeItem("patient_token"); nav("/pasien/masuk"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("patient_token"); toast.success("Keluar dari perangkat ini"); nav("/pasien/masuk"); };
  const logoutAll = async () => {
    try { await patientApi.post("/patient/auth/logout-all"); localStorage.removeItem("patient_token"); toast.success("Keluar dari semua perangkat"); nav("/pasien/masuk"); }
    catch (err) { toast.error(patientErr(err)); }
  };
  const changePw = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await patientApi.put("/patient/change-password", pw);
      if (data.access_token) localStorage.setItem("patient_token", data.access_token);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password diperbarui");
    } catch (err) { toast.error(patientErr(err)); } finally { setSaving(false); }
  };

  if (me === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#FAF7FF] text-slate-400">Memuat…</div>;

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="akun-saya">
      <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <a href="/login" className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#4C1D95]"><Heart size={18} weight="fill" className="text-[#DB2777]" /> HalloGBS</a>
          <button data-testid="ak-logout" onClick={logout} className="flex items-center gap-1.5 text-sm font-bold text-slate-500"><SignOut size={16} weight="bold" /> Keluar</button>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        <section className="rounded-3xl border border-pink-100 bg-white p-6">
          <h1 className="font-heading text-2xl font-extrabold text-[#4C1D95]">Akun Saya</h1>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between border-b border-pink-50 py-2"><span className="text-slate-500">Nama</span><span data-testid="ak-name" className="font-bold text-slate-800">{me.name}</span></div>
            <div className="flex justify-between border-b border-pink-50 py-2"><span className="text-slate-500">Email</span><span data-testid="ak-email" className="font-bold text-slate-800">{me.email}</span></div>
            <div className="flex justify-between py-2"><span className="text-slate-500">Status</span>
              {me.email_verified
                ? <span data-testid="ak-verified" className="inline-flex items-center gap-1 font-bold text-emerald-600"><ShieldCheck size={16} weight="fill" /> Terverifikasi</span>
                : <span data-testid="ak-unverified" className="inline-flex items-center gap-1 font-bold text-amber-600"><WarningCircle size={16} weight="fill" /> Belum diverifikasi</span>}
            </div>
          </div>
        </section>

        <button onClick={() => nav("/koleksi-saya")} data-testid="ak-koleksi-link" className="flex w-full items-center gap-3 rounded-3xl border border-[#7C4DE0]/20 bg-gradient-to-br from-[#F5F0FF] to-[#FDE7F1] p-5 text-left transition-transform hover:-translate-y-0.5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/70"><MusicNotes size={22} weight="duotone" className="text-[#7C4DE0]" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold text-[#7C4DE0]">Koleksi Relaksasi Saya</span>
            <span className="mt-0.5 block text-xs text-slate-500">Dengarkan audio Hypno-Breastfeeding & Hypnobirthing yang Anda miliki.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#7C4DE0]" />
        </button>

        <button onClick={() => nav("/riwayat-pembelian")} data-testid="ak-riwayat-link" className="flex w-full items-center gap-3 rounded-3xl border border-pink-100 bg-white p-5 text-left transition-transform hover:-translate-y-0.5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FDE7F1]"><Receipt size={22} weight="duotone" className="text-[#DB2777]" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold text-[#DB2777]">Riwayat Pembelian</span>
            <span className="mt-0.5 block text-xs text-slate-500">Lihat status pembayaran & pesanan audio digital Anda.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#DB2777]" />
        </button>

        <button onClick={() => nav("/pesanan-belanja")} data-testid="ak-belanja-orders-link" className="flex w-full items-center gap-3 rounded-3xl border border-[#7C4DE0]/20 bg-white p-5 text-left transition-transform hover:-translate-y-0.5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5F0FF]"><Storefront size={22} weight="duotone" className="text-[#7C4DE0]" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold text-[#7C4DE0]">Pesanan Belanja</span>
            <span className="mt-0.5 block text-xs text-slate-500">Lihat status pembayaran & pengiriman pesanan produk Anda.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#7C4DE0]" />
        </button>

        <section className="rounded-3xl border border-pink-100 bg-white p-6">
          <h2 className="mb-4 font-heading text-base font-extrabold text-[#4C1D95]">Ganti Password</h2>
          <form onSubmit={changePw} className="space-y-3">
            <input data-testid="ak-current-pw" type="password" required placeholder="Password saat ini" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className={inputCls} />
            <input data-testid="ak-new-pw" type="password" required minLength={6} placeholder="Password baru (min. 6)" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className={inputCls} />
            <button data-testid="ak-change-pw" disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#7C4DE0] px-5 py-3 font-heading text-sm font-extrabold text-white disabled:opacity-60"><LockKey size={16} weight="bold" /> {saving ? "Menyimpan..." : "Perbarui Password"}</button>
          </form>
        </section>

        <button data-testid="ak-logout-all" onClick={logoutAll} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 font-heading text-sm font-extrabold text-rose-600">
          <Devices size={18} weight="bold" /> Keluar dari Semua Perangkat
        </button>

        <PwaRefreshButton testid="ak-pwa-refresh" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#DDD0FF] bg-[#F5F0FF] px-5 py-3 font-heading text-sm font-extrabold text-[#6A38C7]" />
      </main>
    </div>
  );
}
